import {
  apiGet as authApiGet,
  apiGetRaw as authApiGetRaw,
  apiSend as authApiSend,
} from "./authDevApiClient.js";
import {
  apiGet,
  apiGetAbsoluteUrl,
  apiGetRaw,
  apiSend,
  getOmniWatchBaseUrl,
} from "./client.ts";
import { getNodeApiBaseUrl } from "./adminNodeCameras.ts";
import {
  normalizeCameraVendor,
  type CameraVendor,
} from "../lib/cameraVendor.ts";
import { loadAccountAccess } from "../utils/appRole.js";

export type Camera = {
  id: string;
  name: string;
  /** Hardware vendor: `hikvision` | `seetong` | `hik_vision` (default hikvision). */
  vendor: CameraVendor;
  cam360Rtsp: string | null;
  ptzRtsp: string | null;
  cam360RtspHasEmbeddedCredentials: boolean;
  ptzRtspHasEmbeddedCredentials: boolean;
  onvifEnabled: boolean;
  onvifHost: string | null;
  onvifPort: number | null;
  onvifUsername: string | null;
  hasPassword: boolean;
  zoomMin: number | null;
  zoomMax: number | null;
  panOffsetDeg: number | null;
  tiltOffsetDeg: number | null;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  updatedAt: string;
};

export type CameraCreatePayload = {
  name: string;
  cam360Rtsp?: string;
  ptzRtsp?: string;
  onvifEnabled: boolean;
  onvifHost?: string;
  onvifPort?: number;
  onvifUsername?: string;
  onvifPassword?: string;
  zoomMin?: number;
  zoomMax?: number;
  panOffsetDeg?: number;
  tiltOffsetDeg?: number;
};

export type CameraUpdatePayload = Partial<
  Omit<
    CameraCreatePayload,
    "zoomMin" | "zoomMax" | "panOffsetDeg" | "tiltOffsetDeg"
  >
> & {
  zoomMin?: number | null;
  zoomMax?: number | null;
  panOffsetDeg?: number | null;
  tiltOffsetDeg?: number | null;
};

/** Envelope shape: `{ "success": true, "data": [...] }` or raw array. */
export type CamerasListEnvelope = {
  success?: boolean;
  message?: string;
  data?: unknown;
  error?: unknown;
};

const defaultMockCamerasListEnvelope: CamerasListEnvelope = {
  success: true,
  message: "",
  data: [],
  error: null,
};

function unwrapListPayload(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const o = body as Record<string, unknown>;
    const d = o.data ?? o.results ?? o.Data;
    if (Array.isArray(d)) return d;
  }
  return [];
}

function unwrapEntityPayload(body: unknown): Record<string, unknown> | null {
  if (!body || typeof body !== "object") return null;
  const o = body as Record<string, unknown>;
  const inner = o.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return o;
}

function pickStr(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
}

function pickNum(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickBool(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (typeof v === "string" && v.trim().toLowerCase() === "true") return true;
  return false;
}

/** Normalizes one camera row from GET list/detail (camelCase or snake_case). */
export function normalizeCameraRow(raw: Record<string, unknown>): Camera {
  const id = String(raw.id ?? raw.camera_id ?? "").trim();
  const name = String(raw.name ?? "").trim() || id || "Camera";
  const createdAt =
    pickStr(raw.createdAt) ??
    pickStr(raw.created_at) ??
    new Date().toISOString();
  const updatedAt =
    pickStr(raw.updatedAt) ?? pickStr(raw.updated_at) ?? createdAt;
  const streamUrl = pickStr(raw.streaming_url) ?? pickStr(raw.streamingUrl);
  return {
    id,
    name,
    vendor: normalizeCameraVendor(
      raw.vendor ?? raw.camera_vendor ?? raw.cameraVendor,
    ),
    cam360Rtsp:
      pickStr(raw.cam360Rtsp) ??
      pickStr(raw.cam360_rtsp) ??
      pickStr(raw.cam360RTSP) ??
      streamUrl,
    ptzRtsp: pickStr(raw.ptzRtsp) ?? pickStr(raw.ptz_rtsp) ?? streamUrl,
    onvifEnabled: pickBool(raw.onvifEnabled ?? raw.onvif_enabled),
    onvifHost: pickStr(raw.onvifHost) ?? pickStr(raw.onvif_host),
    onvifPort: pickNum(raw.onvifPort) ?? pickNum(raw.onvif_port),
    onvifUsername: pickStr(raw.onvifUsername) ?? pickStr(raw.onvif_username),
    hasPassword: pickBool(raw.hasPassword ?? raw.has_password),
    cam360RtspHasEmbeddedCredentials: pickBool(
      raw.cam360RtspHasEmbeddedCredentials ??
        raw.cam360_rtsp_has_embedded_credentials,
    ),
    ptzRtspHasEmbeddedCredentials: pickBool(
      raw.ptzRtspHasEmbeddedCredentials ??
        raw.ptz_rtsp_has_embedded_credentials,
    ),
    zoomMin: pickNum(raw.zoomMin ?? raw.zoom_min),
    zoomMax: pickNum(raw.zoomMax ?? raw.zoom_max),
    panOffsetDeg: pickNum(raw.panOffsetDeg ?? raw.pan_offset_deg),
    tiltOffsetDeg: pickNum(raw.tiltOffsetDeg ?? raw.tilt_offset_deg),
    latitude: pickNum(raw.latitude ?? raw.lat),
    longitude: pickNum(raw.longitude ?? raw.lng ?? raw.lon),
    createdAt,
    updatedAt,
  };
}

function filterCamerasForAssignedAccess(cameras: Camera[]): Camera[] {
  const access = loadAccountAccess();
  if (!access || access.isAdmin) return cameras;
  const ids = access.readableCameraIds;
  if (!Array.isArray(ids) || ids.length === 0) return cameras;
  const allowed = new Set(ids.map((id) => String(id)));
  return cameras.filter((c) => allowed.has(String(c.id)));
}

function mapCamerasListBody(body: unknown): Camera[] {
  const rows = unwrapListPayload(body);
  return filterCamerasForAssignedAccess(
    rows
      .filter((r) => r && typeof r === "object")
      .map((r) => normalizeCameraRow(r as Record<string, unknown>))
      .filter((c) => c.id.length > 0),
  );
}

function mapCameraEntityBody(body: unknown): Camera {
  const row = unwrapEntityPayload(body);
  if (!row || !String(row.id ?? row.camera_id ?? "").trim()) {
    throw new Error("Invalid camera response");
  }
  return normalizeCameraRow(row);
}

/** Auth/users backend (`REACT_APP_API_URL_LOGIN_AND_LOGOUT_DEV` / `_LOGIN_AND_LOGOUT`). */
export function usesAuthDevCameraApi(): boolean {
  return (
    String(process.env.REACT_APP_API_URL_LOGIN_AND_LOGOUT_DEV ?? "").trim() !==
      "" ||
    String(process.env.REACT_APP_API_URL_LOGIN_AND_LOGOUT ?? "").trim() !== ""
  );
}

/**
 * Mock / dev-only in-memory cameras for Place360 when no API base URL is set.
 *
 * Live list: **`fetchCameras`** uses GET when any of these is non-empty —
 * **`REACT_APP_WAHANKHEDE_360_API_URL`** / **`REACT_APP_OMNI_WATCH_API_URL`** → `GET …/api/v1/cameras`
 * **`REACT_APP_NODE_API_URL`** (default `http://localhost:5000`) → `GET …/api/cameras`.
 */
const MOCK_CAMERAS_LIST_STORAGE_KEY =
  "place360:mock-responses:api-v1-cameras-list";

const HLS_STREAM_KEY_REPLACEMENTS: ReadonlyArray<readonly [string, string]> = [
  [
    "4f0135fc-6168-4c60-810e-ecd19244afac",
    "341d88e9-f6c8-4aac-8ba2-be621ef776b0",
  ],
];

function remappedHlsStreamKey(cameraId: string): string {
  const key = cameraId.trim();
  for (const [from, to] of HLS_STREAM_KEY_REPLACEMENTS) {
    if (key === from) return to;
  }
  return key;
}

function rewriteLegacyHlsStreamUrls(url: string | null): string | null {
  if (url == null) return null;
  const s = String(url).trim();
  if (s === "") return null;
  let out = s.replace(/\/api\/v1\/streams\//gi, "/api/streams/");
  for (const [from, to] of HLS_STREAM_KEY_REPLACEMENTS) {
    out = out.split(from).join(to);
  }
  return out;
}

function parseRowsFromListEnvelope(
  parsed: unknown,
): Record<string, unknown>[] | null {
  if (!parsed || typeof parsed !== "object") return null;
  const o = parsed as Record<string, unknown>;
  const data = o.data;
  if (!Array.isArray(data)) return null;
  return data.filter(
    (item): item is Record<string, unknown> =>
      Boolean(item) && typeof item === "object" && !Array.isArray(item),
  );
}

function loadInitialMockCameraRows(): Record<string, unknown>[] {
  let fromStorage: Record<string, unknown>[] | null = null;
  if (typeof window !== "undefined") {
    try {
      const raw = window.localStorage.getItem(MOCK_CAMERAS_LIST_STORAGE_KEY);
      if (raw != null && raw !== "") {
        fromStorage = parseRowsFromListEnvelope(JSON.parse(raw) as unknown);
      }
    } catch {
      fromStorage = null;
    }
  }
  if (fromStorage && fromStorage.length > 0) {
    return fromStorage;
  }
  return (
    parseRowsFromListEnvelope(defaultMockCamerasListEnvelope as unknown) ?? []
  );
}

function persistMockCamerasListRows(): void {
  if (typeof window === "undefined") return;
  const envelope: CamerasListEnvelope = {
    success: true,
    message: "",
    data: mockCamerasListRows,
    error: null,
  };
  try {
    window.localStorage.setItem(
      MOCK_CAMERAS_LIST_STORAGE_KEY,
      JSON.stringify(envelope),
    );
  } catch (e) {
    console.warn("mock cameras list: failed to persist to localStorage", e);
  }
}

const mockCamerasListRows: Record<string, unknown>[] =
  loadInitialMockCameraRows();

if (typeof window !== "undefined" && mockCamerasListRows.length > 0) {
  try {
    const raw = window.localStorage.getItem(MOCK_CAMERAS_LIST_STORAGE_KEY);
    if (raw == null || raw === "") {
      persistMockCamerasListRows();
    } else {
      const rows = parseRowsFromListEnvelope(JSON.parse(raw) as unknown);
      if (!rows || rows.length === 0) {
        persistMockCamerasListRows();
      }
    }
  } catch {
    persistMockCamerasListRows();
  }
}

function newCameraId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `cam-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function filterMockRowsBySearch(searchKey: string): Record<string, unknown>[] {
  const q = searchKey.trim().toLowerCase();
  if (!q) return [...mockCamerasListRows];
  return mockCamerasListRows.filter((row) => {
    const name = String(row.name ?? "").toLowerCase();
    return name.includes(q);
  });
}

function fetchLiveCameras(searchKey: string): Promise<Camera[]> {
  const q =
    searchKey.trim().length > 0
      ? `?search=${encodeURIComponent(searchKey.trim())}`
      : "";
  if (getOmniWatchBaseUrl().length > 0) {
    return apiGet<unknown>(`/api/v1/cameras${q}`).then(mapCamerasListBody);
  }
  const nodeBase = getNodeApiBaseUrl();
  const url = /\/api$/i.test(nodeBase)
    ? `${nodeBase}/cameras${q}`
    : `${nodeBase}/api/cameras${q}`;
  return apiGetAbsoluteUrl<unknown>(url).then(mapCamerasListBody);
}

async function fetchLiveCamera(id: string): Promise<Camera> {
  const encoded = encodeURIComponent(id.trim());
  if (getOmniWatchBaseUrl().length > 0) {
    const body = await apiGet<unknown>(`/api/cameras/${encoded}`);
    return mapCameraEntityBody(body);
  }
  const nodeBase = getNodeApiBaseUrl();
  const url = /\/api$/i.test(nodeBase)
    ? `${nodeBase}/cameras/${encoded}`
    : `${nodeBase}/api/cameras/${encoded}`;
  const body = await apiGetAbsoluteUrl<unknown>(url);
  return mapCameraEntityBody(body);
}

export function place360UsesLiveCameraList(): boolean {
  return getOmniWatchBaseUrl().length > 0 || getNodeApiBaseUrl().length > 0;
}

const cameraListInflight = new Map<string, Promise<Camera[]>>();

export async function fetchCameras(search?: string): Promise<Camera[]> {
  const key = search?.trim() ?? "";
  let pending = cameraListInflight.get(key);
  if (pending) return pending;

  if (usesAuthDevCameraApi()) {
    const q = key.length > 0 ? `?search=${encodeURIComponent(key)}` : "";
    pending = authApiGet(`/api/cameras${q}`)
      .then((body) => mapCamerasListBody(body))
      .finally(() => {
        cameraListInflight.delete(key);
      });
  } else if (place360UsesLiveCameraList()) {
    pending = fetchLiveCameras(key).finally(() => {
      cameraListInflight.delete(key);
    });
  } else {
    pending = Promise.resolve(
      mapCamerasListBody({
        success: true,
        message: "",
        data: filterMockRowsBySearch(key),
        error: null,
      }),
    ).finally(() => {
      cameraListInflight.delete(key);
    });
  }

  cameraListInflight.set(key, pending);
  return pending;
}

export async function fetchCamera(id: string): Promise<Camera> {
  if (usesAuthDevCameraApi()) {
    const body = await authApiGet(`/api/cameras/${encodeURIComponent(id)}`);
    return mapCameraEntityBody(body);
  }

  if (place360UsesLiveCameraList()) {
    return fetchLiveCamera(id);
  }

  const row = mockCamerasListRows.find(
    (r) => String(r.id ?? "").trim() === id.trim(),
  );
  if (!row) {
    throw new Error("Camera not found");
  }
  return normalizeCameraRow(row);
}

export async function createCamera(body: CameraCreatePayload): Promise<Camera> {
  if (usesAuthDevCameraApi()) {
    const res = await authApiSend("/api/cameras", "POST", body);
    return mapCameraEntityBody(res);
  }

  const now = new Date().toISOString();
  const id = newCameraId();
  const row: Record<string, unknown> = {
    id,
    name: body.name.trim(),
    cam360Rtsp: body.cam360Rtsp ?? null,
    ptzRtsp: body.ptzRtsp ?? null,
    cam360RtspHasEmbeddedCredentials: false,
    ptzRtspHasEmbeddedCredentials: false,
    onvifEnabled: Boolean(body.onvifEnabled),
    onvifHost: body.onvifHost ?? null,
    onvifPort: body.onvifPort ?? null,
    onvifUsername: body.onvifUsername ?? null,
    hasPassword: Boolean(
      body.onvifPassword && String(body.onvifPassword).trim(),
    ),
    zoomMin: body.zoomMin ?? null,
    zoomMax: body.zoomMax ?? null,
    panOffsetDeg: body.panOffsetDeg ?? null,
    tiltOffsetDeg: body.tiltOffsetDeg ?? null,
    createdAt: now,
    updatedAt: now,
  };
  mockCamerasListRows.push(row);
  persistMockCamerasListRows();
  return normalizeCameraRow(row);
}

/** POST /api/cameras — accepts admin form body (snake_case or camelCase). */
export async function createCameraRaw(
  body: Record<string, unknown>,
): Promise<Camera> {
  const res = await authApiSend("/api/cameras", "POST", body);
  return mapCameraEntityBody(res);
}

export async function updateCamera(
  id: string,
  body: CameraUpdatePayload,
): Promise<Camera> {
  if (usesAuthDevCameraApi()) {
    const res = await authApiSend(
      `/api/cameras/${encodeURIComponent(id)}`,
      "PUT",
      body,
    );
    return mapCameraEntityBody(res);
  }

  const idx = mockCamerasListRows.findIndex(
    (r) => String(r.id ?? "").trim() === id.trim(),
  );
  if (idx < 0) {
    throw new Error("Camera not found");
  }
  const prev = mockCamerasListRows[idx]!;
  const merged: Record<string, unknown> = { ...prev };
  if (body.name != null) merged.name = body.name.trim();
  if (body.cam360Rtsp !== undefined)
    merged.cam360Rtsp = body.cam360Rtsp ?? null;
  if (body.ptzRtsp !== undefined) merged.ptzRtsp = body.ptzRtsp ?? null;
  if (body.onvifEnabled !== undefined) merged.onvifEnabled = body.onvifEnabled;
  if (body.onvifHost !== undefined) merged.onvifHost = body.onvifHost ?? null;
  if (body.onvifPort !== undefined) merged.onvifPort = body.onvifPort ?? null;
  if (body.onvifUsername !== undefined)
    merged.onvifUsername = body.onvifUsername ?? null;
  if (body.zoomMin !== undefined) merged.zoomMin = body.zoomMin;
  if (body.zoomMax !== undefined) merged.zoomMax = body.zoomMax;
  if (body.panOffsetDeg !== undefined) merged.panOffsetDeg = body.panOffsetDeg;
  if (body.tiltOffsetDeg !== undefined)
    merged.tiltOffsetDeg = body.tiltOffsetDeg;
  merged.updatedAt = new Date().toISOString();
  mockCamerasListRows[idx] = merged;
  persistMockCamerasListRows();
  return normalizeCameraRow(merged);
}

/** PUT /api/cameras/:id — accepts admin form body (snake_case or camelCase). */
export async function updateCameraRaw(
  id: string,
  body: Record<string, unknown>,
): Promise<Camera> {
  const res = await authApiSend(
    `/api/cameras/${encodeURIComponent(id)}`,
    "PUT",
    body,
  );
  return mapCameraEntityBody(res);
}

/** Map API camera to administration table row shape. */
export function cameraToAdminRow(cam: Camera): Record<string, unknown> {
  return {
    id: cam.id,
    camera_id: cam.id,
    name: cam.name,
    cam360_rtsp: cam.cam360Rtsp,
    cam360Rtsp: cam.cam360Rtsp,
    streaming_url: cam.cam360Rtsp,
    ptz_rtsp: cam.ptzRtsp,
    ptzRtsp: cam.ptzRtsp,
    onvif_enabled: cam.onvifEnabled,
    onvifEnabled: cam.onvifEnabled,
    onvif_host: cam.onvifHost,
    onvifHost: cam.onvifHost,
    onvif_port: cam.onvifPort,
    onvifPort: cam.onvifPort,
    onvif_username: cam.onvifUsername,
    onvifUsername: cam.onvifUsername,
    zoom_min: cam.zoomMin,
    zoom_max: cam.zoomMax,
    pan_offset_deg: cam.panOffsetDeg,
    tilt_offset_deg: cam.tiltOffsetDeg,
    created_at: cam.createdAt,
    createdAt: cam.createdAt,
  };
}

export async function deleteCamera(id: string): Promise<void> {
  if (usesAuthDevCameraApi()) {
    await authApiSend(`/api/cameras/${encodeURIComponent(id)}`, "DELETE");
    return;
  }

  const idx = mockCamerasListRows.findIndex(
    (r) => String(r.id ?? "").trim() === id.trim(),
  );
  if (idx >= 0) {
    mockCamerasListRows.splice(idx, 1);
    persistMockCamerasListRows();
  }
}

export type CameraRelayUrls = {
  hls360: string | null;
  hlsPtz: string | null;
  webrtc360: string | null;
  webrtcPtz: string | null;
};

export type LiveStreamTransport = "hls" | "webrtc";

/** Default live preview transport for 360° / PTZ relay streams. */
export const DEFAULT_LIVE_STREAM_TRANSPORT: LiveStreamTransport = "webrtc";

export function resolveRelayStreamUrl(
  urls: CameraRelayUrls,
  kind: "360" | "ptz",
  transport: LiveStreamTransport = DEFAULT_LIVE_STREAM_TRANSPORT,
): string | null {
  const hls = kind === "360" ? urls.hls360 : urls.hlsPtz;
  const webrtc = kind === "360" ? urls.webrtc360 : urls.webrtcPtz;
  const h = hls?.trim() || null;
  const w = webrtc?.trim() || null;
  if (transport === "webrtc") {
    return w ?? h;
  }
  return h ?? w;
}

/** Prefer 360° when the relay exposes it, otherwise PTZ. */
export function pickRelayPlaybackUrl(
  urls: CameraRelayUrls,
  transport: LiveStreamTransport = DEFAULT_LIVE_STREAM_TRANSPORT,
): string | null {
  return (
    resolveRelayStreamUrl(urls, "360", transport) ??
    resolveRelayStreamUrl(urls, "ptz", transport)
  );
}

/** Try WebRTC first, then HLS, using whatever relay/start returned. */
export function pickRelayPlaybackWithTransport(
  urls: CameraRelayUrls,
): { url: string; transport: LiveStreamTransport } | null {
  for (const transport of ["webrtc", "hls"] as LiveStreamTransport[]) {
    const url = pickRelayPlaybackUrl(urls, transport);
    if (url) return { url, transport };
  }
  return null;
}

export type CameraPtzState = {
  pan: number;
  tilt: number;
  zoom: number;
};

function mapCameraPtzStateRow(row: Record<string, unknown>): CameraPtzState {
  const pan = Number(row.pan);
  const tilt = Number(row.tilt);
  const zoom = Number(row.zoom);
  return {
    pan: Number.isFinite(pan) ? pan : 0,
    tilt: Number.isFinite(tilt) ? tilt : 0,
    zoom: Number.isFinite(zoom) ? zoom : 1,
  };
}

export async function fetchCameraPtzState(id: string): Promise<CameraPtzState> {
  if (usesAuthDevCameraApi() || place360UsesLiveCameraList()) {
    const body = await (usesAuthDevCameraApi() ? authApiGet : apiGet)(
      `/api/cameras/${encodeURIComponent(id)}/ptz/state`,
    );
    const row = unwrapEntityPayload(body);
    if (!row || typeof row !== "object") {
      throw new Error("Invalid PTZ state response");
    }
    return mapCameraPtzStateRow(row);
  }

  const row = mockCamerasListRows.find(
    (r) => String(r.id ?? "").trim() === id.trim(),
  );
  if (!row) {
    throw new Error("Camera not found");
  }
  return {
    pan: 0,
    tilt: 15,
    zoom: 1,
  };
}

function mapRelayStartBody(res: unknown): CameraRelayUrls {
  const row = unwrapEntityPayload(res) ?? (res as Record<string, unknown>);
  return {
    hls360: rewriteLegacyHlsStreamUrls(
      pickStr(row.hls360 ?? row.hls_360) ?? null,
    ),
    hlsPtz: rewriteLegacyHlsStreamUrls(
      pickStr(row.hlsPtz ?? row.hls_ptz) ?? null,
    ),
    webrtc360: pickStr(row.webrtc360 ?? row.webrtc_360) ?? null,
    webrtcPtz: pickStr(row.webrtcPtz ?? row.webrtc_ptz) ?? null,
  };
}

export async function startCameraRelay(id: string): Promise<CameraRelayUrls> {
  if (usesAuthDevCameraApi() || place360UsesLiveCameraList()) {
    const res = await (usesAuthDevCameraApi() ? authApiSend : apiSend)(
      `/api/cameras/${encodeURIComponent(id)}/relay/start`,
      "POST",
    );
    return mapRelayStartBody(res);
  }

  const row = mockCamerasListRows.find(
    (r) => String(r.id ?? "").trim() === id.trim(),
  );
  if (!row) {
    throw new Error("Camera not found");
  }
  const enc = encodeURIComponent(remappedHlsStreamKey(id));
  const hls360 = `/api/streams/hls/${enc}/360/index.m3u8`;
  const hlsPtz = `/api/streams/hls/${enc}/ptz/index.m3u8`;
  void unwrapEntityPayload({ data: row });
  return {
    hls360: rewriteLegacyHlsStreamUrls(hls360),
    hlsPtz: rewriteLegacyHlsStreamUrls(hlsPtz),
    webrtc360: null,
    webrtcPtz: null,
  };
}

export async function fetchCameraSnapshotBlob(
  id: string,
  cacheBust?: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const q =
    cacheBust !== undefined && cacheBust !== ""
      ? `?t=${encodeURIComponent(cacheBust)}`
      : "";
  const getRaw = usesAuthDevCameraApi() ? authApiGetRaw : apiGetRaw;
  const res = await getRaw(
    `/api/cameras/${encodeURIComponent(id)}/snapshot${q}`,
    { signal },
  );
  if (!res.ok) {
    throw new Error(`Snapshot failed: ${res.status}`);
  }
  const blob = await res.blob();
  if (blob.size === 0) {
    throw new Error("Snapshot empty");
  }
  const ct = (blob.type || "").toLowerCase();
  if (ct && !ct.startsWith("image/")) {
    throw new Error(`Snapshot not an image (${ct})`);
  }
  return blob;
}

const snapshotResolved = new Map<string, Blob>();
const snapshotInflight = new Map<string, Promise<Blob>>();

function snapshotCacheKey(id: string, version: string): string {
  return `${id}\0${version}`;
}

export function fetchCameraSnapshotBlobCached(
  id: string,
  version: string,
): Promise<Blob> {
  const key = snapshotCacheKey(id, version);
  const hit = snapshotResolved.get(key);
  if (hit) return Promise.resolve(hit);

  let p = snapshotInflight.get(key);
  if (p) return p;

  p = fetchCameraSnapshotBlob(id, version, undefined)
    .then((blob) => {
      const prefix = `${id}\0`;
      for (const k of snapshotResolved.keys()) {
        if (k.startsWith(prefix)) snapshotResolved.delete(k);
      }
      snapshotResolved.set(key, blob);
      return blob;
    })
    .finally(() => {
      snapshotInflight.delete(key);
    });

  snapshotInflight.set(key, p);
  return p;
}
